// src/pages/admin/AdminAnalytics.tsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import "./AdminAnalytics.css";


// =====================================================
// API
// =====================================================

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";


// =====================================================
// TYPES
// =====================================================

type AnalyticsRange =
  | "today"
  | "7d"
  | "30d"
  | "90d"
  | "all";


interface Overview {
  totalVisitors: number;
  registeredVisitors: number;
  guestVisitors: number;
  totalSessions: number;
  gamesStarted: number;
  gamesCompleted: number;
  registrations: number;
  logins: number;
}


interface GameModes {
  ai: number;
  friend: number;
  online: number;
}


interface VisitorBreakdown {
  new: number;
  returning: number;
}


interface Conversion {
  guestVisitors: number;
  convertedVisitors: number;
  rate: number;
}


interface ActivityPoint {
  bucket: string;
  visitors: number;
  sessions: number;
}


interface DevicePoint {
  deviceType: string;
  visitors: number;
}


interface VisitorLocation {
  countryCode: string;
  countryName: string;
  region: string;
  city: string;
  uniqueVisitors: number;
}


interface PlayerLocation {
  countryCode: string;
  countryName: string;
  region: string;
  city: string;
  uniquePlayers: number;
  gamesStarted: number;
}


interface Geography {
  visitors: VisitorLocation[];
  players: PlayerLocation[];
}


interface AnalyticsResponse {
  success: boolean;
  range?: AnalyticsRange;
  overview?: Overview;
  gameModes?: GameModes;
  visitors?: VisitorBreakdown;
  conversion?: Conversion;
  activity?: ActivityPoint[];
  devices?: DevicePoint[];
  geography?: Geography;
  message?: string;
}


// =====================================================
// EMPTY DATA
// =====================================================

const EMPTY_OVERVIEW: Overview = {
  totalVisitors: 0,
  registeredVisitors: 0,
  guestVisitors: 0,
  totalSessions: 0,
  gamesStarted: 0,
  gamesCompleted: 0,
  registrations: 0,
  logins: 0,
};


const EMPTY_GAME_MODES: GameModes = {
  ai: 0,
  friend: 0,
  online: 0,
};


const EMPTY_VISITORS: VisitorBreakdown = {
  new: 0,
  returning: 0,
};


const EMPTY_CONVERSION: Conversion = {
  guestVisitors: 0,
  convertedVisitors: 0,
  rate: 0,
};


const EMPTY_GEOGRAPHY: Geography = {
  visitors: [],
  players: [],
};


// =====================================================
// HELPERS
// =====================================================

const formatNumber = (
  value: number
) => {

  return new Intl.NumberFormat(
    undefined
  ).format(value || 0);
};


const formatBucket = (
  value: string,
  range: AnalyticsRange
) => {

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  if (range === "today") {
    return date.toLocaleTimeString(
      undefined,
      {
        hour: "numeric",
      }
    );
  }

  return date.toLocaleDateString(
    undefined,
    {
      month: "short",
      day: "numeric",
    }
  );
};


const percent = (
  value: number,
  total: number
) => {

  if (total <= 0) {
    return 0;
  }

  return Math.round(
    (value / total) * 100
  );
};


// =====================================================
// ACTIVITY CHART
// =====================================================

const ActivityChart: React.FC<{
  points: ActivityPoint[];
  range: AnalyticsRange;
}> = ({
  points,
  range,
}) => {

  const width = 900;
  const height = 290;

  const padding = {
    top: 24,
    right: 20,
    bottom: 42,
    left: 48,
  };


  const innerWidth =
    width -
    padding.left -
    padding.right;

  const innerHeight =
    height -
    padding.top -
    padding.bottom;


  const maxValue =
    Math.max(
      1,
      ...points.map(
        (point) =>
          Math.max(
            point.visitors,
            point.sessions
          )
      )
    );


  const coordinates =
    points.map(
      (
        point,
        index
      ) => {

        const x =
          points.length <= 1
            ? padding.left +
              innerWidth / 2
            : padding.left +
              (
                index /
                (points.length - 1)
              ) *
                innerWidth;

        const visitorY =
          padding.top +
          innerHeight -
          (
            point.visitors /
            maxValue
          ) *
            innerHeight;

        const sessionY =
          padding.top +
          innerHeight -
          (
            point.sessions /
            maxValue
          ) *
            innerHeight;

        return {
          ...point,
          x,
          visitorY,
          sessionY,
        };
      }
    );


  const visitorPath =
    coordinates
      .map(
        (
          point,
          index
        ) =>
          `${
            index === 0
              ? "M"
              : "L"
          } ${point.x} ${point.visitorY}`
      )
      .join(" ");


  const sessionPath =
    coordinates
      .map(
        (
          point,
          index
        ) =>
          `${
            index === 0
              ? "M"
              : "L"
          } ${point.x} ${point.sessionY}`
      )
      .join(" ");


  if (points.length === 0) {

    return (
      <div className="analytics-empty-chart">
        <span>◇</span>

        <strong>
          No visitor activity yet
        </strong>

        <p>
          Activity will appear here as
          visitors use P-ARENA.
        </p>
      </div>
    );
  }


  const labelIndexes =
    new Set<number>();

  if (points.length > 0) {
    labelIndexes.add(0);
    labelIndexes.add(
      points.length - 1
    );

    labelIndexes.add(
      Math.floor(
        (points.length - 1) / 2
      )
    );
  }


  return (
    <div className="analytics-chart-scroll">

      <svg
        className="analytics-line-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Visitor activity chart"
      >

        {/* GRID */}

        {[0, 0.25, 0.5, 0.75, 1].map(
          (ratio) => {

            const y =
              padding.top +
              innerHeight -
              ratio *
                innerHeight;

            const value =
              Math.round(
                maxValue *
                  ratio
              );

            return (
              <g
                key={ratio}
              >
                <line
                  x1={padding.left}
                  y1={y}
                  x2={
                    width -
                    padding.right
                  }
                  y2={y}
                  className="analytics-chart-grid"
                />

                <text
                  x={
                    padding.left -
                    12
                  }
                  y={y + 4}
                  textAnchor="end"
                  className="analytics-chart-axis"
                >
                  {value}
                </text>
              </g>
            );
          }
        )}


        {/* VISITORS */}

        <path
          d={visitorPath}
          className="analytics-chart-visitors"
        />


        {/* SESSIONS */}

        <path
          d={sessionPath}
          className="analytics-chart-sessions"
        />


        {/* POINTS */}

        {coordinates.map(
          (
            point,
            index
          ) => (
            <g key={point.bucket}>

              <circle
                cx={point.x}
                cy={point.visitorY}
                r="4"
                className="analytics-chart-dot analytics-chart-dot-visitors"
              />

              <circle
                cx={point.x}
                cy={point.sessionY}
                r="3"
                className="analytics-chart-dot analytics-chart-dot-sessions"
              />

              {labelIndexes.has(
                index
              ) && (
                <text
                  x={point.x}
                  y={
                    height - 12
                  }
                  textAnchor={
                    index === 0
                      ? "start"
                      : index ===
                          points.length -
                            1
                        ? "end"
                        : "middle"
                  }
                  className="analytics-chart-axis"
                >
                  {formatBucket(
                    point.bucket,
                    range
                  )}
                </text>
              )}

            </g>
          )
        )}

      </svg>

    </div>
  );
};


// =====================================================
// DONUT
// =====================================================

const Donut: React.FC<{
  value: number;
  total: number;
  label: string;
}> = ({
  value,
  total,
  label,
}) => {

  const percentage =
    percent(
      value,
      total
    );

  return (
    <div
      className="analytics-donut"
      style={{
        background:
          `conic-gradient(
            #d9ad55 0% ${percentage}%,
            rgba(255,255,255,0.07)
              ${percentage}% 100%
          )`,
      }}
    >
      <div className="analytics-donut-center">
        <strong>
          {percentage}%
        </strong>

        <span>
          {label}
        </span>
      </div>
    </div>
  );
};


// =====================================================
// ADMIN ANALYTICS
// =====================================================

const AdminAnalytics:
React.FC = () => {

  const navigate =
    useNavigate();


  const [
    range,
    setRange,
  ] =
    useState<AnalyticsRange>(
      "30d"
    );


  const [
    overview,
    setOverview,
  ] =
    useState<Overview>(
      EMPTY_OVERVIEW
    );


  const [
    gameModes,
    setGameModes,
  ] =
    useState<GameModes>(
      EMPTY_GAME_MODES
    );


  const [
    visitors,
    setVisitors,
  ] =
    useState<VisitorBreakdown>(
      EMPTY_VISITORS
    );


  const [
    conversion,
    setConversion,
  ] =
    useState<Conversion>(
      EMPTY_CONVERSION
    );


  const [
    activity,
    setActivity,
  ] =
    useState<ActivityPoint[]>(
      []
    );


  const [
    devices,
    setDevices,
  ] =
    useState<DevicePoint[]>(
      []
    );


  const [
    geography,
    setGeography,
  ] =
    useState<Geography>(
      EMPTY_GEOGRAPHY
    );


  const [
    loading,
    setLoading,
  ] =
    useState(true);


  const [
    refreshing,
    setRefreshing,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  // ===================================================
  // AUTH FAILURE
  // ===================================================

  const handleAuthenticationFailure =
    useCallback(
      (
        status: number
      ) => {

        if (status === 401) {

          localStorage.removeItem(
            "token"
          );

          navigate(
            "/login",
            {
              replace: true,

              state: {
                returnTo:
                  "/admin/analytics",
              },
            }
          );

          return true;
        }


        if (status === 403) {

          navigate(
            "/",
            {
              replace: true,
            }
          );

          return true;
        }


        return false;
      },
      [
        navigate,
      ]
    );


  // ===================================================
  // LOAD ANALYTICS
  // ===================================================

  const loadAnalytics =
    useCallback(
      async (
        isRefresh = false
      ) => {

        const token =
          localStorage.getItem(
            "token"
          );


        if (!token) {

          navigate(
            "/login",
            {
              replace: true,

              state: {
                returnTo:
                  "/admin/analytics",
              },
            }
          );

          return;
        }


        try {

          if (isRefresh) {
            setRefreshing(true);
          } else {
            setLoading(true);
          }

          setError("");


          const response =
            await fetch(
              `${API_BASE_URL}/api/admin/analytics?range=${range}`,
              {
                method: "GET",

                headers: {
                  Authorization:
                    `Bearer ${token}`,
                },
              }
            );


          if (
            handleAuthenticationFailure(
              response.status
            )
          ) {
            return;
          }


          const data:
            AnalyticsResponse =
              await response.json();


          if (
            !response.ok ||
            !data.success
          ) {
            throw new Error(
              data.message ||
                "Unable to load analytics."
            );
          }


          setOverview(
            data.overview ||
              EMPTY_OVERVIEW
          );

          setGameModes(
            data.gameModes ||
              EMPTY_GAME_MODES
          );

          setVisitors(
            data.visitors ||
              EMPTY_VISITORS
          );

          setConversion(
            data.conversion ||
              EMPTY_CONVERSION
          );

          setActivity(
            data.activity || []
          );

          setDevices(
            data.devices || []
          );

          setGeography(
            data.geography ||
              EMPTY_GEOGRAPHY
          );

        } catch (err) {

          console.error(
            "Admin analytics request failed:",
            err
          );

          setError(
            err instanceof Error
              ? err.message
              : "Unable to load analytics."
          );

        } finally {

          setLoading(false);
          setRefreshing(false);

        }
      },
      [
        handleAuthenticationFailure,
        navigate,
        range,
      ]
    );


  useEffect(
    () => {
      void loadAnalytics();
    },
    [
      loadAnalytics,
    ]
  );


  // ===================================================
  // DERIVED VALUES
  // ===================================================

  const totalModes =
    gameModes.ai +
    gameModes.friend +
    gameModes.online;


  const totalVisitorTypes =
    visitors.new +
    visitors.returning;


  const registeredPercent =
    percent(
      overview.registeredVisitors,
      overview.totalVisitors
    );


  const guestPercent =
    percent(
      overview.guestVisitors,
      overview.totalVisitors
    );


  const completionRate =
    percent(
      overview.gamesCompleted,
      overview.gamesStarted
    );


  const deviceMax =
    useMemo(
      () =>
        Math.max(
          1,
          ...devices.map(
            (device) =>
              device.visitors
          )
        ),
      [
        devices,
      ]
    );


  // ===================================================
  // RETURN
  // ===================================================

  return (
    <div className="admin-analytics-page">

      {/* ============================================= */}
      {/* HEADER                                        */}
      {/* ============================================= */}

      <header className="admin-analytics-header">

        <div className="admin-analytics-header-inner">

          <div className="admin-analytics-brand">

            <Link
              to="/"
              className="admin-analytics-logo"
            >
              ♠ P-<span>ARENA</span>
            </Link>

            <span className="admin-analytics-divider">
              /
            </span>

            <span className="admin-analytics-section-name">
              Admin Analytics
            </span>

          </div>


          <nav className="admin-analytics-nav">

            <Link
              to="/admin/analytics"
              className="admin-analytics-nav-link active"
            >
              Analytics
            </Link>

            <Link
              to="/admin/support"
              className="admin-analytics-nav-link"
            >
              Support
            </Link>

            <Link
              to="/"
              className="admin-analytics-house-link"
            >
              Game House
            </Link>

          </nav>

        </div>

      </header>


      {/* ============================================= */}
      {/* HERO                                          */}
      {/* ============================================= */}

      <section className="admin-analytics-hero">

        <div className="admin-analytics-hero-glow" />

        <div className="admin-analytics-container admin-analytics-hero-row">

          <div>

            <p className="admin-analytics-eyebrow">
              P-ARENA Intelligence
            </p>

            <h1>
              Visitor Analytics
            </h1>

            <p className="admin-analytics-description">
              Understand who is visiting P-ARENA,
              how players are using Fasoor, and how
              guests convert into registered players.
            </p>

          </div>


          <div className="admin-analytics-actions">

            <select
              value={range}
              onChange={(event) =>
                setRange(
                  event.target
                    .value as AnalyticsRange
                )
              }
              className="admin-analytics-range"
              aria-label="Analytics date range"
            >
              <option value="today">
                Today
              </option>

              <option value="7d">
                Last 7 Days
              </option>

              <option value="30d">
                Last 30 Days
              </option>

              <option value="90d">
                Last 90 Days
              </option>

              <option value="all">
                All Time
              </option>
            </select>


            <button
              type="button"
              className="admin-analytics-refresh"
              disabled={refreshing}
              onClick={() =>
                void loadAnalytics(
                  true
                )
              }
            >
              {refreshing
                ? "Refreshing..."
                : "Refresh"}
            </button>

          </div>

        </div>

      </section>


      {/* ============================================= */}
      {/* MAIN                                          */}
      {/* ============================================= */}

      <main className="admin-analytics-container admin-analytics-main">

        {error && (
          <div className="admin-analytics-error">
            {error}
          </div>
        )}


        {loading ? (

          <div className="admin-analytics-loading">

            <div className="admin-analytics-loader" />

            <span>
              Loading P-ARENA analytics...
            </span>

          </div>

        ) : (

          <>

            {/* ======================================= */}
            {/* PRIMARY STATISTICS                      */}
            {/* ======================================= */}

            <section className="admin-analytics-stats">

              <article className="analytics-stat-card analytics-stat-primary">

                <span>
                  Total Visitors
                </span>

                <strong>
                  {formatNumber(
                    overview.totalVisitors
                  )}
                </strong>

                <small>
                  Unique P-ARENA visitors
                </small>

              </article>


              <article className="analytics-stat-card">

                <span>
                  Registered
                </span>

                <strong>
                  {formatNumber(
                    overview.registeredVisitors
                  )}
                </strong>

                <small>
                  {registeredPercent}% of visitors
                </small>

              </article>


              <article className="analytics-stat-card">

                <span>
                  Guests
                </span>

                <strong>
                  {formatNumber(
                    overview.guestVisitors
                  )}
                </strong>

                <small>
                  {guestPercent}% of visitors
                </small>

              </article>


              <article className="analytics-stat-card">

                <span>
                  Games Started
                </span>

                <strong>
                  {formatNumber(
                    overview.gamesStarted
                  )}
                </strong>

                <small>
                  Fasoor game sessions
                </small>

              </article>


              <article className="analytics-stat-card">

                <span>
                  Sessions
                </span>

                <strong>
                  {formatNumber(
                    overview.totalSessions
                  )}
                </strong>

                <small>
                  Visitor sessions
                </small>

              </article>


              <article className="analytics-stat-card">

                <span>
                  Registrations
                </span>

                <strong>
                  {formatNumber(
                    overview.registrations
                  )}
                </strong>

                <small>
                  Accounts created
                </small>

              </article>

            </section>


            {/* ======================================= */}
            {/* ACTIVITY                                 */}
            {/* ======================================= */}

            <section className="analytics-panel analytics-activity-panel">

              <div className="analytics-panel-header">

                <div>
                  <span className="analytics-panel-kicker">
                    Traffic
                  </span>

                  <h2>
                    Visitor Activity
                  </h2>
                </div>


                <div className="analytics-chart-legend">

                  <span>
                    <i className="legend-visitors" />
                    Visitors
                  </span>

                  <span>
                    <i className="legend-sessions" />
                    Sessions
                  </span>

                </div>

              </div>


              <ActivityChart
                points={activity}
                range={range}
              />

            </section>


            {/* ======================================= */}
            {/* INSIGHTS GRID                           */}
            {/* ======================================= */}

            <section className="analytics-insights-grid">

              {/* REGISTERED VS GUEST */}

              <article className="analytics-panel analytics-insight-card">

                <div className="analytics-panel-header">

                  <div>
                    <span className="analytics-panel-kicker">
                      Audience
                    </span>

                    <h2>
                      Registered vs Guest
                    </h2>
                  </div>

                </div>


                <div className="analytics-donut-layout">

                  <Donut
                    value={
                      overview.registeredVisitors
                    }
                    total={
                      overview.totalVisitors
                    }
                    label="Registered"
                  />


                  <div className="analytics-breakdown">

                    <div>
                      <span>
                        Registered
                      </span>

                      <strong>
                        {formatNumber(
                          overview.registeredVisitors
                        )}
                      </strong>

                      <small>
                        {registeredPercent}%
                      </small>
                    </div>


                    <div>
                      <span>
                        Guests
                      </span>

                      <strong>
                        {formatNumber(
                          overview.guestVisitors
                        )}
                      </strong>

                      <small>
                        {guestPercent}%
                      </small>
                    </div>

                  </div>

                </div>

              </article>


              {/* GAME MODES */}

              <article className="analytics-panel analytics-insight-card">

                <div className="analytics-panel-header">

                  <div>
                    <span className="analytics-panel-kicker">
                      Fasoor
                    </span>

                    <h2>
                      Game Modes
                    </h2>
                  </div>

                </div>


                <div className="analytics-bars">

                  {[
                    {
                      label: "Play with AI",
                      value:
                        gameModes.ai,
                    },
                    {
                      label: "Friend",
                      value:
                        gameModes.friend,
                    },
                    {
                      label: "Online",
                      value:
                        gameModes.online,
                    },
                  ].map(
                    (item) => {

                      const percentage =
                        percent(
                          item.value,
                          totalModes
                        );

                      return (
                        <div
                          className="analytics-bar-row"
                          key={item.label}
                        >

                          <div className="analytics-bar-meta">

                            <span>
                              {item.label}
                            </span>

                            <strong>
                              {formatNumber(
                                item.value
                              )}
                              {" · "}
                              {percentage}%
                            </strong>

                          </div>


                          <div className="analytics-bar-track">

                            <div
                              className="analytics-bar-fill"
                              style={{
                                width:
                                  `${percentage}%`,
                              }}
                            />

                          </div>

                        </div>
                      );
                    }
                  )}

                </div>

              </article>


              {/* NEW / RETURNING */}

              <article className="analytics-panel analytics-insight-card">

                <div className="analytics-panel-header">

                  <div>
                    <span className="analytics-panel-kicker">
                      Loyalty
                    </span>

                    <h2>
                      New vs Returning
                    </h2>
                  </div>

                </div>


                <div className="analytics-donut-layout">

                  <Donut
                    value={
                      visitors.returning
                    }
                    total={
                      totalVisitorTypes
                    }
                    label="Returning"
                  />


                  <div className="analytics-breakdown">

                    <div>
                      <span>
                        New
                      </span>

                      <strong>
                        {formatNumber(
                          visitors.new
                        )}
                      </strong>

                      <small>
                        {percent(
                          visitors.new,
                          totalVisitorTypes
                        )}%
                      </small>
                    </div>


                    <div>
                      <span>
                        Returning
                      </span>

                      <strong>
                        {formatNumber(
                          visitors.returning
                        )}
                      </strong>

                      <small>
                        {percent(
                          visitors.returning,
                          totalVisitorTypes
                        )}%
                      </small>
                    </div>

                  </div>

                </div>

              </article>


              {/* CONVERSION */}

              <article className="analytics-panel analytics-insight-card analytics-conversion-card">

                <div className="analytics-panel-header">

                  <div>
                    <span className="analytics-panel-kicker">
                      Growth
                    </span>

                    <h2>
                      Guest Conversion
                    </h2>
                  </div>

                </div>


                <div className="analytics-conversion-value">
                  {conversion.rate}%
                </div>

                <p className="analytics-conversion-copy">
                  Guest visitors who later became
                  registered P-ARENA players.
                </p>


                <div className="analytics-conversion-stats">

                  <div>
                    <span>
                      Guest Visitors
                    </span>

                    <strong>
                      {formatNumber(
                        conversion.guestVisitors
                      )}
                    </strong>
                  </div>


                  <div>
                    <span>
                      Converted
                    </span>

                    <strong>
                      {formatNumber(
                        conversion.convertedVisitors
                      )}
                    </strong>
                  </div>

                </div>

              </article>

            </section>


            {/* ======================================= */}
            {/* SECONDARY INFORMATION                   */}
            {/* ======================================= */}

            <section className="analytics-secondary-grid">

              <article className="analytics-panel">

                <div className="analytics-panel-header">

                  <div>
                    <span className="analytics-panel-kicker">
                      Devices
                    </span>

                    <h2>
                      Visitor Devices
                    </h2>
                  </div>

                </div>


                <div className="analytics-device-list">

                  {devices.length === 0 ? (

                    <div className="analytics-small-empty">
                      No device data yet.
                    </div>

                  ) : (

                    devices.map(
                      (device) => {

                        const barWidth =
                          Math.round(
                            (
                              device.visitors /
                              deviceMax
                            ) *
                              100
                          );

                        return (
                          <div
                            className="analytics-device-row"
                            key={
                              device.deviceType
                            }
                          >

                            <div className="analytics-device-meta">

                              <span>
                                {device.deviceType}
                              </span>

                              <strong>
                                {formatNumber(
                                  device.visitors
                                )}
                              </strong>

                            </div>


                            <div className="analytics-device-track">

                              <div
                                className="analytics-device-fill"
                                style={{
                                  width:
                                    `${barWidth}%`,
                                }}
                              />

                            </div>

                          </div>
                        );
                      }
                    )

                  )}

                </div>

              </article>


              <article className="analytics-panel">

                <div className="analytics-panel-header">

                  <div>
                    <span className="analytics-panel-kicker">
                      Performance
                    </span>

                    <h2>
                      Game Engagement
                    </h2>
                  </div>

                </div>


                <div className="analytics-engagement-grid">

                  <div>
                    <span>
                      Games Started
                    </span>

                    <strong>
                      {formatNumber(
                        overview.gamesStarted
                      )}
                    </strong>
                  </div>


                  <div>
                    <span>
                      Games Completed
                    </span>

                    <strong>
                      {formatNumber(
                        overview.gamesCompleted
                      )}
                    </strong>
                  </div>


                  <div>
                    <span>
                      Completion Rate
                    </span>

                    <strong>
                      {completionRate}%
                    </strong>
                  </div>


                  <div>
                    <span>
                      Logins
                    </span>

                    <strong>
                      {formatNumber(
                        overview.logins
                      )}
                    </strong>
                  </div>

                </div>

              </article>

            </section>


            {/* ======================================= */}
            {/* PLAYER GEOGRAPHY                        */}
            {/* ======================================= */}

            <section className="analytics-panel analytics-location-panel">

              <div className="analytics-panel-header">

                <div>
                  <span className="analytics-panel-kicker">
                    Geography
                  </span>

                  <h2>
                    Player Locations
                  </h2>
                </div>

                <div className="analytics-location-summary">
                  <span>
                    Unique players are deduplicated
                  </span>
                </div>

              </div>


              {geography.players.length === 0 ? (

                <div className="analytics-location-empty">
                  No player location data yet.
                </div>

              ) : (

                <div className="analytics-location-table-wrap">

                  <table className="analytics-location-table">

                    <thead>
                      <tr>
                        <th>
                          Country
                        </th>

                        <th>
                          State / Region
                        </th>

                        <th>
                          City
                        </th>

                        <th className="analytics-location-number">
                          Unique Players
                        </th>

                        <th className="analytics-location-number">
                          Games Played
                        </th>
                      </tr>
                    </thead>

                    <tbody>

                      {geography.players.map(
                        (
                          location,
                          index
                        ) => {

                          const country =
                            location.countryName ===
                              "Unknown"
                              ? "—"
                              : location.countryName;

                          const region =
                            location.region ===
                              "Unknown"
                              ? "—"
                              : location.region;

                          const city =
                            location.city ===
                              "Unknown"
                              ? "—"
                              : location.city;

                          return (
                            <tr
                              key={[
                                location.countryCode,
                                location.region,
                                location.city,
                                index,
                              ].join("-")}
                            >

                              <td>
                                <div className="analytics-location-country">

                                  <span>
                                    {country}
                                  </span>

                                  {location.countryCode !==
                                    "XX" && (
                                    <small>
                                      {location.countryCode}
                                    </small>
                                  )}

                                </div>
                              </td>

                              <td>
                                {region}
                              </td>

                              <td>
                                {city}
                              </td>

                              <td className="analytics-location-number">
                                <strong>
                                  {formatNumber(
                                    location.uniquePlayers
                                  )}
                                </strong>
                              </td>

                              <td className="analytics-location-number">
                                {formatNumber(
                                  location.gamesStarted
                                )}
                              </td>

                            </tr>
                          );
                        }
                      )}

                    </tbody>

                  </table>

                </div>

              )}


              <p className="analytics-location-note">
                Registered players are counted once by
                account. Guest players are counted once
                using a privacy-safe anonymous network
                identifier. Location is approximate and
                may be unavailable for some players.
              </p>

            </section>

          </>

        )}

      </main>

    </div>
  );
};


export default AdminAnalytics;
